import * as React from 'react'

import {
  DEFAULT_SCHEDULE_OPTIONS,
  SCHEDULE_OPTIONS_FOR_APPROVAL_REQUIRED,
  SCHEDULE_OPTIONS_FOR_EDIT_MODES,
} from './constants'
import type { ScheduleOption, ScheduleSelectorState } from './types'
import type { ComposerMode } from '../../../stores/types'

type UseScheduleSelectorProps = {
  defaultOption?: ScheduleOption
  initialScheduledAt?: number | null
  isApprovalRequired?: boolean
  onChange?: (value: ScheduleSelectorState) => void
  composerMode?: ComposerMode
}

type UseScheduleSelectorReturn = {
  options: ScheduleOption[]
  scheduleSelectorValue: ScheduleSelectorState
  onScheduleSelectorChange: (value: ScheduleSelectorState) => void
  isScheduleSelectionValid: boolean
  fallbackOption: ScheduleOption
}

export function useScheduleSelector({
  defaultOption = 'Queue',
  initialScheduledAt = null,
  isApprovalRequired = false,
  composerMode = 'new-post',
  onChange,
}: UseScheduleSelectorProps = {}): UseScheduleSelectorReturn {
  const hasSetInitialValue = React.useRef(false)

  let optionsToShow = DEFAULT_SCHEDULE_OPTIONS

  if (composerMode !== 'new-post') {
    optionsToShow = SCHEDULE_OPTIONS_FOR_EDIT_MODES
  } else if (isApprovalRequired) {
    optionsToShow = SCHEDULE_OPTIONS_FOR_APPROVAL_REQUIRED
  }

  const isDefaultOptionValid = optionsToShow.includes(defaultOption)
  const fallbackOption = optionsToShow[0] || 'FixedTime'

  const defaultState = React.useMemo(() => {
    return {
      option: isDefaultOptionValid ? defaultOption : fallbackOption,
      scheduledAt: null,
    }
  }, [defaultOption, fallbackOption, isDefaultOptionValid])

  const fallbackState = React.useMemo(() => {
    return {
      option: fallbackOption,
      scheduledAt: null,
    }
  }, [fallbackOption])

  const [currentValue, setCurrentValue] =
    React.useState<ScheduleSelectorState>(defaultState)

  React.useEffect(() => {
    // we need to update the value if the initialScheduledAt changes
    if (initialScheduledAt) {
      setCurrentValue({
        option: 'FixedTime',
        scheduledAt: initialScheduledAt,
      })
    } else {
      setCurrentValue(hasSetInitialValue.current ? fallbackState : defaultState)
    }
    hasSetInitialValue.current = true
  }, [initialScheduledAt, defaultState, fallbackState])

  const onScheduleSelectorChange = React.useCallback(
    (value: ScheduleSelectorState): void => {
      const newValue: ScheduleSelectorState =
        value.option === 'FixedTime'
          ? {
              option: 'FixedTime',
              scheduledAt: value.scheduledAt,
            }
          : {
              option: value.option,
              scheduledAt: null,
            }
      setCurrentValue(newValue)
      onChange?.(newValue)
    },
    [onChange],
  )

  const isScheduleSelectionValid =
    currentValue.option === 'FixedTime'
      ? Boolean(currentValue.scheduledAt)
      : Boolean(currentValue.option)

  return {
    options: optionsToShow,
    scheduleSelectorValue: currentValue,
    onScheduleSelectorChange,
    isScheduleSelectionValid,
    fallbackOption,
  }
}
